import SkeletonElement from "@/components/Shares/SkeletonElement";
import { IProductVariant } from "@/interfaces/product";
import Link from "next/link";
import { useState, useEffect } from "react";
import {
  Button,
  Image,
  Modal,
  ModalContent,
  useDisclosure,
} from "@nextui-org/react";
import { QuickView } from "@/components/Pages/CategoryListPage";
import { isEmpty, isEqual } from "lodash";
import { formatPrice } from "@/utils/format";
import { Rating, Star } from "@smastrom/react-rating";
import { AnimatePresence, motion } from "framer-motion";

interface ProductCardProps {
  link: string;
  productVariants: IProductVariant[];
  newArrival: boolean;
  size?: "sm" | "md" | "lg";
  hoverEffect?: boolean;
}

const ProductCard: React.FC<ProductCardProps> = (props) => {
  const {
    link,
    productVariants,
    newArrival,
    size = "lg",
    hoverEffect = true,
  } = props;
  const [selectedVariant, setSelectedVariant] = useState<IProductVariant>(
    productVariants[0],
  );
  const [showQuickView, setShowQuickView] = useState(false);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const sizeClasses: Record<string, string> = {
    lg: "h-80",
    md: "h-60",
    sm: "h-40",
  };

  // Preload all images
  useEffect(() => {
    const preloadImages = () => {
      productVariants.forEach((variant) => {
        // Preload the first and last images
        if (variant.images.length > 0) {
          const firstImage = new window.Image();
          firstImage.src = variant.images[0];

          if (variant.images.length > 1) {
            const lastImage = new window.Image();
            lastImage.src = variant.images[variant.images.length - 1];
          }
        }
      });
    };

    preloadImages();
  }, [productVariants]);

  return (
    <>
      <div
        className={`flex flex-col gap-4 pb-2 w-full items-center border border-transparent transition-all duration-500 ${
          hoverEffect && "hover:border-icon"
        }`}
      >
        <div
          className={`${
            sizeClasses[size] || ""
          } relative overflow-hidden w-full flex items-center justify-center bg-white`}
          onMouseEnter={() => setShowQuickView(true)}
          onMouseLeave={() => setShowQuickView(false)}
        >
          {selectedVariant.oldPrice !== 0 && (
            <div className="absolute z-20 size-16 right-8 top-8">
              <Image src="/sale-icon.png" alt="sales icon" />
            </div>
          )}
          {newArrival && (
            <div className="absolute flex justify-center bg-icon text-white z-20 px-2 rounded-full py-1 text-semibold left-8 top-8">
              New
            </div>
          )}
          {showQuickView ? (
            <Link href={link} className="select-none">
              <AnimatePresence mode="wait">
                <motion.img
                  key={
                    selectedVariant.images[selectedVariant.images.length - 1]
                  }
                  src={
                    selectedVariant.images[selectedVariant.images.length - 1]
                  }
                  alt={selectedVariant.name}
                  className="absolute top-0 left-0 h-full w-full object-cover"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </AnimatePresence>
            </Link>
          ) : (
            <Link href={link} className="p-12 select-none">
              <AnimatePresence mode="wait">
                <motion.img
                  key={selectedVariant.images[0]}
                  src={selectedVariant.images[0]}
                  alt={selectedVariant.name}
                  className="max-h-full max-w-full object-contain"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </AnimatePresence>
            </Link>
          )}

          {showQuickView && hoverEffect && (
            <Button
              radius="sm"
              className="absolute m-auto z-20 px-8 text-white shadow-lg bg-icon transition-all duration-500"
              onPress={onOpen}
            >
              Quick View
            </Button>
          )}
        </div>
        <div className="flex flex-col gap-[2px] items-center">
          <Link href={link}>
            <p className="hover:text-icon transition-all duration-500 text-center">
              {selectedVariant.name}
            </p>
          </Link>
          <div className="flex gap-2">
            <p
              className={`${
                selectedVariant.oldPrice !== 0 ? "text-red-500" : "text-icon"
              } `}
            >
              {formatPrice(selectedVariant.price)}
            </p>
            {selectedVariant.oldPrice !== 0 && (
              <div className="flex gap-2">
                <p className={"line-through text-gray-500 font-light"}>
                  <span>{formatPrice(selectedVariant.oldPrice)}</span>
                </p>
                <div className="bg-red-500 text-white px-2 rounded-md">
                  <p>
                    -
                    {Math.round(
                      ((selectedVariant.oldPrice - selectedVariant.price) *
                        100) /
                        selectedVariant.oldPrice,
                    )}
                    %
                  </p>
                </div>
              </div>
            )}
          </div>
          {productVariants.length > 1 && (
            <div className="flex gap-2">
              {productVariants.map((variant) => {
                const { id, color } = variant;
                return (
                  <div
                    key={id}
                    className={`size-8 rounded-full flex items-center justify-center border select-none cursor-pointer ${
                      isEqual(selectedVariant.id, id)
                        ? "border-icon border-[2.5px]"
                        : "border-white"
                    }`}
                  >
                    <div
                      style={{ backgroundColor: `${color}` }}
                      className={`size-6 border rounded-full`}
                      onClick={() => setSelectedVariant(variant)}
                    />
                  </div>
                );
              })}
            </div>
          )}
          {!isEmpty(selectedVariant.reviews) && (
            <Link href={link}>
              <div className="flex gap-2">
                <Rating
                  readOnly
                  value={
                    selectedVariant.reviews.reduce(
                      (sum, review) => sum + review.rate,
                      0,
                    ) / selectedVariant.reviews.length
                  }
                  style={{ maxWidth: 80 }}
                  itemStyles={{
                    itemShapes: Star,
                    activeFillColor: "#B79B6C",
                    inactiveFillColor: "#FFEACF",
                  }}
                />
                <p className="text-gray-400">
                  ({selectedVariant.reviews.length})
                </p>
              </div>
            </Link>
          )}
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="5xl"
        aria-label="Quick View"
      >
        <ModalContent>
          <QuickView productVariants={productVariants} link={link} />
        </ModalContent>
      </Modal>
    </>
  );
};

export const ProductCardSkeleton = () => {
  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="h-80 w-full flex items-center justify-center bg-gray-100">
        <SkeletonElement />
      </div>
      <div className="flex flex-col gap-[2px]">
        <SkeletonElement />
        <SkeletonElement />
      </div>
    </div>
  );
};
export default ProductCard;
